/**
 * FileGridView — Card Grid Layout for Files & Folders
 *
 * Renders files and folders as a 4-column grid of cards.
 *
 * Figma layout:
 * - Root level: "Folders" section (with count) + "Files" section (with count), separated
 * - Inside a folder: single mixed grid
 * - Pagination at the bottom
 */

import { useMemo } from "react";
import Pagination from "../../../../../../../../components/Table/Pagination";
import FileGridCard from "./FileGridCard";
import FileGridViewShimmer from "./Shimmer/FileGridViewShimmer";
import type { KBFileEntity } from "../types/fileManagement.types";
import { isFolder } from "../utils/fileManagement.utils";

interface FileGridViewProps {
  /** All items (files + folders) from the API for the current page. */
  items: KBFileEntity[];
  /** Whether the API is currently loading. */
  isFetching: boolean;
  /** Called when user clicks on a folder card. */
  onFolderClick: (folder: KBFileEntity) => void;
  /** Called when user clicks on a file card to open its details. */
  onFileClick: (file: KBFileEntity) => void;
  /** Called when a row action is selected from the three-dots menu. */
  onRowAction: (item: KBFileEntity, action: string) => void;
  /** Whether we're inside a folder (true) or at root level (false). */
  isInsideFolder: boolean;
  /** Pagination: current page. */
  currentPage: number;
  /** Pagination: rows per page. */
  rowsPerPage: number;
  /** Pagination: total pages. */
  totalPages: number;
  /** Called when page changes. */
  onPageChange: (page: number) => void;
  /** Called when rows per page changes. */
  onRowsPerPageChange: (size: number) => void;
}

const FileGridView = ({
  items,
  isFetching,
  onFolderClick,
  onFileClick,
  onRowAction,
  isInsideFolder,
  currentPage,
  rowsPerPage,
  totalPages,
  onPageChange,
  onRowsPerPageChange,
}: FileGridViewProps) => {
  const { folders, files } = useMemo(() => {
    const folderItems = items.filter(isFolder);
    const fileItems = items.filter((item) => !isFolder(item));
    return { folders: folderItems, files: fileItems };
  }, [items]);

  const renderContent = () => {
    if (isFetching) {
      return <FileGridViewShimmer />;
    }

    if (items.length === 0) {
      return (
        <div className="flex items-center justify-center h-[400px]">
          <span className="text-md font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
            No files or folders found
          </span>
        </div>
      );
    }

    return (
      <>
        {folders.length > 0 && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-1.5">
              <h4 className="text-lg font-medium text-heading-text dark:text-heading-text-dk">
                Folders
              </h4>
              <span className="border border-gray-100 dark:border-gray-100-dk rounded px-1 py-0.5 text-xxs font-semibold text-verba-indigo dark:text-verba-indigo-dk leading-normal">
                {folders?.length}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {folders.map((folder) => (
                <FileGridCard
                  key={folder._id}
                  item={folder}
                  onFolderClick={onFolderClick}
                  onFileClick={onFileClick}
                  onRowAction={onRowAction}
                />
              ))}
            </div>
          </div>
        )}

        {files?.length > 0 && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-1.5">
              <h4 className="text-lg font-medium text-heading-text dark:text-heading-text-dk">
                Files
              </h4>
              <span className="border border-gray-100 dark:border-gray-100-dk rounded px-1 py-0.5 text-xxs font-semibold text-verba-indigo dark:text-verba-indigo-dk leading-normal">
                {files.length}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {files.map((file) => (
                <FileGridCard
                  key={file._id}
                  item={file}
                  onFolderClick={onFolderClick}
                  onFileClick={onFileClick}
                  onRowAction={onRowAction}
                />
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex flex-col gap-5">
      {renderContent()}

      {totalPages > 0 && (
        <Pagination
          rowsPerPage={rowsPerPage}
          handleRowsPerPageChange={onRowsPerPageChange}
          currentPage={currentPage}
          handlePageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </div>
  );
};

export default FileGridView;
